/**
 * Authentication context: types, error contract, JWT helper, context object and
 * consumer hooks. The AuthProvider + ProtectedRoute components live in
 * AuthProvider.tsx, and the network calls in authApi.ts, so this module stays
 * free of react-refresh/only-export-components warnings.
 */
import { createContext, useContext } from 'react';

// Error codes for structured auth error handling
export type AuthErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'EMAIL_EXISTS'
  | 'EMAIL_INVALID'
  | 'PASSWORD_WEAK'
  | 'MISSING_FIELDS'
  | 'NETWORK_ERROR'
  | 'LOGIN_FAILED'
  | 'PROFILE_FETCH_FAILED'
  | 'REGISTRATION_CLOSED'
  | 'REGISTRATION_INVITATION_REQUIRED'
  | 'REGISTRATION_INVITATION_INVALID'
  | 'REGISTRATION_EMAIL_DELIVERY_FAILED'
  | 'REGISTRATION_FAILED';

export class AuthError extends Error {
  public readonly code: AuthErrorCode;
  constructor(code: AuthErrorCode, message: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

// Types
export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: 'admin' | 'standard' | 'viewer';
  organization?: string;
  is_active_customer: boolean;
}

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  organization?: string;
  sentry_consent: boolean;
  invite_code?: string;
}

export type RegistrationMode = 'closed' | 'invite' | 'open';

export interface RegistrationCapability {
  mode: RegistrationMode;
  registration_available: boolean;
  invitation_required: boolean;
}

export interface AuthContextType {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  getAuthHeaders: () => Record<string, string>;
  checkAuth: () => void;
  registrationCapability?: RegistrationCapability | null;
  registrationCapabilityError?: boolean;
}

/**
 * Decode the JWT access token locally and check exp - 30s > now.
 * Eliminates the POST /token/verify/ network round-trip on cold load.
 * Server-side revocation is still caught by the 401 interceptor in client.ts.
 *
 * Exported for unit testing (pure parser, no I/O).
 */
export function isJwtValid(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    if (typeof payload.exp !== 'number') return false;
    return payload.exp - 30 > Date.now() / 1000;
  } catch {
    return false;
  }
}

// Create context
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Alias for consistency
export const useAuthContext = useAuth;
